import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useOutletContext } from "react-router";
import { AppShell } from "../components/AppShell";
import { DivisionChips } from "../components/DivisionChips";
import { PageHeader } from "../components/PageHeader";
import { SubNav } from "../components/SubNav";
import LabourApprovals from "./labour/Approvals";
import LabourCost from "./labour/Cost";
import LabourReconciliation from "./labour/Reconciliation";
import { useLabourBoard } from "../lib/api";

/** Project labour costing (WP-24 wave L, HRX-03), split into screens rather than stacked.
 *
 * Rates were the first half of this and are done: `Activity Cost` held zero rows and every activity type
 * carried a zero rate, so 1,299 timesheet lines produced no labour cost at all. What that exposed is the rest —
 * nobody had ever approved any of it, and there was nothing to check the hours against. */

export const LABOUR_SCREENS = ["approvals", "reconciliation"] as const;

export default function Labour() {
  const { t } = useTranslation();
  const [company, setCompany] = useState("");
  // the layout asks for the board so the company chips exist before any screen has loaded; the cost screen
  // reads the same query key, so this costs one request rather than two
  const board = useLabourBoard();
  const companies = board.data?.companies ?? [];

  const items = [
    { key: "cost", to: "/projects/labour", label: t("lab.tabs.cost"), end: true },
    ...LABOUR_SCREENS.map((k) => ({ key: k, to: `/projects/labour/${k}`, label: t(`lab.tabs.${k}`) })),
  ];

  return (
    <AppShell subtitle={t("lab.title")}>
      <PageHeader title={t("lab.title")} subtitle={t("lab.subtitle")}
        stamp={board.data ? <span className="text-xs text-brand-grey">{t("proj.asOf")} {board.data.as_of}</span> : undefined} />
      {companies.length > 1 && (
        <div className="mt-4"><DivisionChips companies={companies} value={company} onChange={setCompany} allLabel={t("exec.allDivisions")} /></div>
      )}
      <SubNav items={items} label={t("lab.sections")} />
      <Outlet context={{ company }} />
    </AppShell>
  );
}

const useCompany = () => useOutletContext<{ company: string }>().company;

export const LabourIndex = () => <LabourCost company={useCompany()} />;
export const LabourQueue = () => <LabourApprovals company={useCompany()} />;
export const LabourReconcile = () => <LabourReconciliation company={useCompany()} />;
